import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useContext,
} from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import secureAxios from "../../utils/secureAxios";
import { v4 as uuidv4 } from "uuid";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import RoutingControl from "./RoutingControl";

import "leaflet/dist/leaflet.css";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import { formatDate } from "../../utils/dateFormatter";

// For Google Maps API - we need regular axios
const axios = require("axios");

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Purpose of Use Selector Component (keeping for potential future use but not using in this file)
const PurposeOfUseSelector = ({ value, onChange, required = false }) => (
  <div className="col-md-3">
    <label className="form-label">
      Purpose of Access {required && <span className="text-danger">*</span>}
    </label>
    <select
      className="form-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
    >
      <option value="">-- Select Purpose --</option>
      <option value="treatment">Treatment/Service Delivery</option>
      <option value="payment">Payment/Billing</option>
      <option value="operations">Healthcare Operations</option>
      <option value="coordination">Care Coordination</option>
    </select>
    <small className="text-muted">Required by HIPAA for PHI access</small>
  </div>
);

// PHI Access Notice Component
const PHIAccessNotice = () => (
  <div className="alert alert-sm alert-info">
    <small>
      <i className="bi bi-shield-lock me-1"></i>
      Accessing consumer information creates an audit log entry for HIPAA
      compliance. Only access information necessary for authorized work
      purposes.
    </small>
  </div>
);

function SortableItem({ wp, index, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: wp.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Helper function to get entity type badge
  const getEntityTypeBadge = (entityType) => {
    switch (entityType) {
      case "user":
        return <span className="badge bg-primary ms-2">Job Coach</span>;
      case "consumer":
        return <span className="badge bg-success ms-2">Consumer</span>;
      case "location":
        return <span className="badge bg-info ms-2">Location</span>;
      default:
        return null;
    }
  };

  // Helper function to get entity name display
  const getEntityDisplay = (wp) => {
    if (wp.entityName) {
      return `(${wp.entityName})`;
    }
    return "";
  };

  return (
    <li
      ref={setNodeRef}
      {...attributes}
      className="list-group-item d-flex justify-content-between align-items-center"
      style={style}
    >
      <span {...listeners} className="me-3" style={{ cursor: "grab" }}>
        <i className="bi bi-grip-vertical"></i>
      </span>

      <div className="flex-grow-1">
        <div>
          <strong>Stop {index + 1}:</strong>{" "}
          {wp.address || `${wp.lat}, ${wp.lng}`}
        </div>
        <div className="d-flex align-items-center mt-1">
          {wp.entityType ? (
            <>
              {getEntityTypeBadge(wp.entityType)}
              {wp.entityName && (
                <small className="text-muted ms-2">
                  {getEntityDisplay(wp)}
                </small>
              )}
            </>
          ) : (
            <span className="badge bg-secondary ms-2">Legacy Stop</span>
          )}
        </div>
      </div>

      <button className="btn btn-sm btn-danger" onClick={() => onRemove(wp.id)}>
        Remove
      </button>
    </li>
  );
}

const RoutePlanner = ({
  editId,
  onFinish,
  prefillConsumerId,
  prefillDate,
  prefillUserId,
}) => {
  const { user } = useContext(AuthContext);

  const [waypoints, setWaypoints] = useState([]);
  const [distance, setDistance] = useState(null);
  const [routeTime, setRouteTime] = useState(null);
  const [routeName, setRouteName] = useState("");
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [selectedSavedRouteId, setSelectedSavedRouteId] = useState("");

  const [users, setUsers] = useState([]);
  const [consumers, setConsumers] = useState([]);
  const [locations, setLocations] = useState([]);

  const [selectedCoach, setSelectedCoach] = useState(
    prefillUserId || user.user_id
  );
  const [selectedConsumer, setSelectedConsumer] = useState(
    prefillConsumerId || ""
  );
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedAssignedConsumer, setSelectedAssignedConsumer] = useState("");

  //mileage
  const [tripDate, setTripDate] = useState(
    () => prefillDate || new Date().toISOString().split("T")[0]
  );
  const [selectedMileageConsumer, setSelectedMileageConsumer] = useState(
    prefillConsumerId || ""
  );
  const [selectedAuthorization, setSelectedAuthorization] = useState("");
  const [authOptions, setAuthOptions] = useState([]);
  const [mileageMessage, setMileageMessage] = useState("");
  const [assignedConsumerId, setAssignedConsumerId] = useState(null);
  const [loadedRouteId, setLoadedRouteId] = useState(null);
  const [selectedRouteId, setSelectedRouteId] = useState("");
  const [reasonForTravel, setReasonForTravel] = useState("");
  const [isBillable, setIsBillable] = useState(true);
  const [authorizationWarning, setAuthorizationWarning] = useState(null);
  const [purposeOfUse, setPurposeOfUse] = useState("treatment"); // Default to treatment (Travel for Service)
  const [showAllConsumers, setShowAllConsumers] = useState(false); // Admin toggle to show all consumers

  const mapRef = useRef();
  const routingRef = useRef();

  // Load existing mileage log data when editId is provided
  useEffect(() => {
    const loadExistingMileageLog = async () => {
      if (!editId) return;

      try {
        console.log("Loading existing mileage log for editing:", editId);

        // Fetch the mileage log data
        const response = await secureAxios.get(`/api/mileage/${editId}`);
        const logData = response.data;

        console.log("Loaded mileage log data:", logData);

        // Populate the form fields with existing data
        setTripDate(
          logData.trip_date
            ? logData.trip_date.split("T")[0]
            : new Date().toISOString().split("T")[0]
        );
        setSelectedCoach(logData.user_id || user.user_id);
        setReasonForTravel(logData.reason_for_travel || "");
        setIsBillable(logData.is_billable !== false);

        // Set consumer and authorization if billable
        if (logData.is_billable && logData.consumer_id) {
          setSelectedMileageConsumer(logData.consumer_id);
          setAssignedConsumerId(logData.consumer_id);

          // Load authorizations for this consumer
          await fetchAuthorizations(logData.consumer_id);

          // Set the authorization
          if (logData.authorization_id) {
            setSelectedAuthorization(logData.authorization_id);
          }
        }

        // Load the route if it exists
        // Load the route if it exists
        if (logData.route_id) {
          setLoadedRouteId(logData.route_id);
          try {
            const routeResponse = await secureAxios.get(
              `/api/routes/${logData.route_id}`
            );
            const routeData = routeResponse.data;

            if (routeData.stops) {
              const stops =
                typeof routeData.stops === "string"
                  ? JSON.parse(routeData.stops)
                  : routeData.stops;

              // Add IDs if missing and preserve entity type info
              const processedStops = stops.map((stop) => ({
                ...stop,
                id: stop.id || uuidv4(),
                // Preserve existing entity type info if it exists
                entityType: stop.entityType || null,
                entityId: stop.entityId || null,
                entityName: stop.entityName || null,
              }));

              setWaypoints(processedStops);
              setDistance(routeData.total_miles);
              setRouteName(routeData.route_name || "");
            }
          } catch (routeErr) {
            console.warn("Could not load route details:", routeErr);
          }
        } else if (logData.stops) {
          // Load stops directly from mileage log if no route_id
          const stops =
            typeof logData.stops === "string"
              ? JSON.parse(logData.stops)
              : logData.stops;
          if (Array.isArray(stops)) {
            const processedStops = stops.map((stop) => ({
              ...stop,
              id: stop.id || uuidv4(),
              entityType: stop.entityType || null,
              entityId: stop.entityId || null,
              entityName: stop.entityName || null,
            }));

            setWaypoints(processedStops);
            setDistance(logData.total_miles);
          }
        }

        setMileageMessage(
          `Editing mileage log from ${formatDate(logData.trip_date)}`
        );
      } catch (err) {
        console.error("Error loading mileage log for editing:", err);
        setMileageMessage("Error loading mileage log data for editing.");
      }
    };

    loadExistingMileageLog();
  }, [editId, user.user_id]); // Re-run when editId changes

  // Handle prefill from Log Sync Check widget
  useEffect(() => {
    // If we have a prefillUserId (from the widget), set the coach first
    // This will trigger the consumers useEffect to load the right consumers
    if (prefillUserId && !editId) {
      console.log("Prefilling user_id (job coach):", prefillUserId);
      setSelectedCoach(prefillUserId);
    }

    if (prefillDate && !editId) {
      setTripDate(prefillDate);
    }
  }, [prefillUserId, prefillDate, editId]);

  // Separate effect to set consumer after consumers are loaded
  useEffect(() => {
    if (prefillConsumerId && !editId && consumers.length > 0) {
      // Check if the prefilled consumer is in the list
      const consumerExists = consumers.some(
        (c) => String(c.consumer_id) === String(prefillConsumerId)
      );

      if (consumerExists) {
        console.log("Prefilling consumer_id:", prefillConsumerId);
        // Set consumer for both route stops section and mileage log section
        setSelectedConsumer(prefillConsumerId); // For "Add Route Stops" dropdown
        setSelectedMileageConsumer(prefillConsumerId); // For "Mileage Log" section
        setIsBillable(true);
        fetchAuthorizations(prefillConsumerId);
      } else {
        console.warn(
          "Prefilled consumer not found in consumer list. Consumer may not be assigned to this job coach."
        );
        // For admins, auto-enable "show all consumers" if consumer isn't in the list
        if (user?.is_admin && !showAllConsumers) {
          console.log(
            "Admin: enabling 'show all consumers' to find prefilled consumer"
          );
          setShowAllConsumers(true);
        }
      }
    }
  }, [
    prefillConsumerId,
    editId,
    consumers.length,
    user?.is_admin,
    showAllConsumers,
  ]);

  useEffect(() => {
    secureAxios
      .get("/api/users", {
        params: {
          tenant_id: user?.tenant_id,
          role: "Job Coach",
        },
      })
      .then((res) => setUsers(res.data))
      .catch((err) => {
        // Handle error silently
      });
  }, []);

  useEffect(() => {
    if (!user?.tenant_id || !user?.user_id || !user?.role) return;

    secureAxios
      .get("/api/locations", {
        params: {
          tenant_id: user.tenant_id,
          user_id: user.user_id,
          user_role:
            user.role || user.user_role || user?.roles?.[0]?.role || "unknown",
        },
      })
      .then((res) => {
        const seen = new Set();
        const unique = res.data.filter((loc) => {
          if (seen.has(loc.location_id)) return false;
          seen.add(loc.location_id);
          return true;
        });
        setLocations(unique);
      })
      .catch((err) => {
        // Handle error silently
      });
  }, [user]);

  useEffect(() => {
    // Admin with "Show All Consumers" toggle enabled
    if (user?.is_admin && showAllConsumers) {
      console.log("Admin loading ALL consumers for tenant");
      secureAxios
        .get("/api/consumers", {
          params: {
            tenant_id: user.tenant_id,
          },
        })
        .then((res) => {
          const seen = new Set();
          const uniqueConsumers = res.data.filter((c) => {
            const statusLower = (c.status || "").toLowerCase();
            const isActive = !["inactive", "archived", "deleted"].includes(
              statusLower
            );
            if (!isActive || seen.has(c.consumer_id)) return false;
            seen.add(c.consumer_id);
            return true;
          });
          setConsumers(uniqueConsumers);
          console.log(
            `[OK] Loaded ${uniqueConsumers.length} active consumers (all)`
          );
        })
        .catch((err) => {
          console.error("Error loading all consumers:", err);
        });
      return;
    }

    // Standard behavior: load consumers assigned to selected coach
    if (selectedCoach) {
      secureAxios
        .get(
          `/api/job-coach-assignments/job-coach-assigned-consumers/${selectedCoach}`
        )
        .then((res) => {
          const seen = new Set();
          const uniqueConsumers = res.data.filter((c) => {
            const isActive = !c.end_date;
            const isConsumerActive = c.status_name === "Active"; // Add status check
            if (!isActive || !isConsumerActive || seen.has(c.consumer_id))
              return false;
            seen.add(c.consumer_id);
            return true;
          });
          setConsumers(uniqueConsumers);
        })
        .catch((err) => {
          // Handle error silently
        });
    } else {
      setConsumers([]);
    }
  }, [selectedCoach, showAllConsumers, user?.is_admin, user?.tenant_id]);

  useEffect(() => {
    secureAxios
      .get(`/api/routes/${user.user_id}`)
      .then((res) => setSavedRoutes(res.data))
      .catch((err) => {
        // Handle error silently
      });
  }, [user.user_id]);

  const handleLoadRoute = async () => {
    if (!selectedSavedRouteId) return;
    const res = await secureAxios.get(
      `/api/routes/details/${selectedSavedRouteId}`
    );
    const { route_name, stops } = res.data;
    setRouteName(route_name);
    setWaypoints(stops);
  };

  const handleDeleteRoute = async () => {
    if (!selectedSavedRouteId) return;

    if (!window.confirm("Are you sure you want to delete this route?")) return;

    try {
      await secureAxios.delete(`/api/routes/${selectedSavedRouteId}`);
      alert("Route deleted.");

      setSavedRoutes((prev) =>
        prev.filter((r) => r.route_id !== selectedSavedRouteId)
      );
      setSelectedSavedRouteId("");
    } catch (err) {
      alert("Failed to delete route.");
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = waypoints.findIndex((wp) => wp.id === active.id);
    const newIndex = waypoints.findIndex((wp) => wp.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      setWaypoints((items) => arrayMove(items, oldIndex, newIndex));
    }
  };

  const handleAddStop = async (entityType, entityId) => {
    try {
      const res = await secureAxios.get(
        `/api/routes/address/${entityType}/${entityId}`
      );
      let { latitude, longitude, address_line_1, city, postal_code, state } =
        res.data;

      if (!latitude || !longitude) {
        const fullAddress = `${address_line_1}, ${city}, ${state} ${postal_code}`;

        const geoRes = await axios.get(
          "https://maps.googleapis.com/maps/api/geocode/json",
          {
            params: {
              address: fullAddress,
              key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
            },
          }
        );

        if (geoRes.data.results && geoRes.data.results.length > 0) {
          latitude = geoRes.data.results[0].geometry.location.lat;
          longitude = geoRes.data.results[0].geometry.location.lng;
        }

        if (geoRes.data && geoRes.data.length > 0) {
          latitude = geoRes.data[0].lat;
          longitude = geoRes.data[0].lon;

          await secureAxios.put(
            `/api/routes/address/${entityType}/${entityId}`,
            {
              latitude,
              longitude,
            }
          );
        } else {
          alert("Geocoding failed: address not found.");
          return;
        }
      }

      if (!latitude || !longitude) {
        alert("Stop is missing valid coordinates and cannot be added.");
        return;
      }

      // Get entity name for display
      let entityName = "";
      if (entityType === "user") {
        const user = users.find((u) => u.user_id === entityId);
        entityName = user?.name || "Job Coach";
      } else if (entityType === "consumer") {
        const consumer = consumers.find((c) => c.consumer_id === entityId);
        entityName = consumer?.name || "Consumer";
      } else if (entityType === "location") {
        const location = locations.find((l) => l.location_id === entityId);
        entityName = location?.location_name || "Location";
      }

      setWaypoints((prev) => [
        ...prev,
        {
          id: uuidv4(),
          lat: parseFloat(latitude),
          lng: parseFloat(longitude),
          address: `${address_line_1}, ${city}, ${state} ${postal_code}`,
          entityType: entityType,
          entityId: entityId,
          entityName: entityName,
        },
      ]);
    } catch (err) {
      alert("Error adding stop. Please check the address and try again.");
    }
  };

  const resetMileageFields = () => {
    setTripDate(new Date().toISOString().split("T")[0]);
    setSelectedMileageConsumer("");
    setSelectedAuthorization("");
    setAuthOptions([]);
    setMileageMessage("");
    setReasonForTravel("");
    setAssignedConsumerId(null);
    setLoadedRouteId(null);
    setIsBillable(true);
    setAuthorizationWarning(null);
    setPurposeOfUse("treatment"); // Reset to default

    setWaypoints([]);
    setDistance(null);
    setRouteTime(null);

    if (mapRef.current) {
      mapRef.current.eachLayer((layer) => {
        if (
          layer._container?.classList?.contains("leaflet-routing-container")
        ) {
          layer.remove();
        }
        if (layer._path) {
          try {
            mapRef.current.removeLayer(layer);
          } catch (err) {
            // Handle error silently
          }
        }
      });

      const instructionsEl = document.getElementById("routing-instructions");
      if (instructionsEl) instructionsEl.innerHTML = "";

      mapRef.current.setView([38.4192, -82.4452], 12);
    }

    routingRef.current?.clearRoute();
    setRouteName("");
    setSelectedConsumer("");
    setSelectedLocation("");
    setSelectedAssignedConsumer("");
    setSelectedSavedRouteId("");
  };

  const handleRemoveStop = (idToRemove) => {
    setWaypoints((prev) => prev.filter((wp) => wp.id !== idToRemove));
  };

  const handleRouteFound = useCallback((miles, minutes) => {
    setDistance(miles);
    setRouteTime(minutes);
  }, []);

  const handleSaveRoute = async () => {
    if (!routeName.trim()) {
      alert("Please enter a route name.");
      return;
    }

    if (waypoints.length < 2) {
      alert("Please add at least two stops to calculate a route.");
      return;
    }

    if (distance === null) {
      alert("Route distance is not ready yet. Please wait a few seconds.");
      return;
    }

    if (!selectedAssignedConsumer) {
      alert("Please select a consumer to assign the route.");
      return;
    }

    const routeData = {
      route_name: routeName,
      total_miles: parseFloat(distance),
      stops: waypoints,
      created_by: user.user_id,
      tenant_id: user.tenant_id,
    };

    try {
      const routeRes = await secureAxios.post("/api/routes", routeData);
      const route_id = routeRes.data.route_id;
      setLoadedRouteId(route_id);

      await secureAxios.post("/api/routes/route-consumer-logs", {
        route_id,
        consumer_id: selectedAssignedConsumer,
        miles: parseFloat(distance),
      });

      alert("Route saved and assigned to consumer!");

      const refreshed = await secureAxios.get(`/api/routes/${user.user_id}`);
      setSavedRoutes(refreshed.data);
      setSelectedSavedRouteId(route_id);
    } catch (err) {
      alert("Failed to save route or assign to consumer.");
    }

    setRouteName("");
    setWaypoints([]);
    setDistance(null);
    setRouteTime(null);
    setSelectedConsumer("");
    setSelectedAssignedConsumer("");
  };

  // Check authorization availability when authorization or miles change
  const checkAuthorizationUsage = async (authId, miles) => {
    if (!authId || !miles || !isBillable) {
      setAuthorizationWarning(null);
      return;
    }

    try {
      const response = await secureAxios.get(
        "/api/mileage/check-authorization",
        {
          params: {
            authorization_id: authId,
            miles_to_add: miles,
          },
        }
      );

      // Always set the warning data to show remaining miles
      setAuthorizationWarning(response.data);
    } catch (err) {
      // Handle error silently
    }
  };

  // Watch for changes in authorization and distance
  useEffect(() => {
    if (selectedAuthorization && distance && isBillable) {
      checkAuthorizationUsage(selectedAuthorization, Math.ceil(distance));
    }
  }, [selectedAuthorization, distance, isBillable]);

  //Mileage
  const handleSaveMileageLog = async () => {
    const consumer_id = assignedConsumerId || selectedMileageConsumer;

    // Validate required fields based on billable status
    if (isBillable && (!consumer_id || !selectedAuthorization)) {
      setMileageMessage("Missing required fields for billable mileage.");
      return;
    }

    if (!tripDate || !selectedCoach) {
      setMileageMessage("Trip date and job coach are required.");
      return;
    }

    // Reason for travel is always required
    if (!reasonForTravel || !reasonForTravel.trim()) {
      setMileageMessage("Reason for travel is required.");
      return;
    }

    // Determine the miles to save
    let milesToSave = Math.ceil(distance || 0);

    // Check for authorization warning and adjust miles if needed
    if (
      authorizationWarning &&
      !authorizationWarning.valid &&
      authorizationWarning.remaining > 0
    ) {
      const confirmPartial = window.confirm(
        `You only have ${authorizationWarning.remaining.toFixed(
          1
        )} miles left on this authorization.\n\n` +
          `Do you want to save only ${authorizationWarning.remaining.toFixed(
            1
          )} miles instead of ${milesToSave} miles?`
      );

      if (!confirmPartial) {
        return;
      }

      // Adjust miles to save to the remaining amount
      milesToSave = Math.floor(authorizationWarning.remaining);
    }

    const mileagePayload = {
      route_id: loadedRouteId || null,
      stops: waypoints,
      consumer_id: isBillable ? consumer_id : null,
      user_id: selectedCoach,
      authorization_id: isBillable ? selectedAuthorization : null,
      trip_date: tripDate,
      total_miles: milesToSave, // Use adjusted miles
      tenant_id: user.tenant_id,
      reason_for_travel: reasonForTravel,
      is_billable: isBillable,
      purpose_of_use: isBillable ? purposeOfUse : "operations",
    };

    try {
      let response;

      if (editId) {
        // Update existing mileage log
        response = await secureAxios.put(
          `/api/mileage/${editId}`,
          mileagePayload
        );
        setMileageMessage(
          `Mileage log updated successfully! (${milesToSave} miles logged)`
        );
      } else {
        // Create new mileage log
        response = await secureAxios.post("/api/mileage", mileagePayload);
        setMileageMessage(
          `Mileage log saved successfully! (${milesToSave} miles logged)`
        );
      }

      // Call the onFinish callback immediately to switch to the list tab
      if (typeof onFinish === "function") {
        onFinish();
      }

      // Reset fields after a short delay
      setTimeout(() => {
        resetMileageFields();
      }, 500);
    } catch (err) {
      if (err.response?.data?.error) {
        setMileageMessage(err.response.data.error);
      } else {
        setMileageMessage("Failed to save mileage log.");
      }
    }
  };

  const fetchAuthorizations = async (consumer_id) => {
    try {
      const url = `/api/auths/consumer/${consumer_id}`;

      const res = await secureAxios.get(url, {
        params: {
          auth_type: "mileage",
          tenant_id: user.tenant_id,
        },
      });

      if (Array.isArray(res.data)) {
        const mileageOnly = res.data.filter(
          (auth) => auth.auth_type === "mileage" && auth.complete !== true
        );
        setAuthOptions(mileageOnly);
      } else {
        setAuthOptions([]);
      }
    } catch (err) {
      setAuthOptions([]);
    }
  };

  const loadRouteDetails = async (routeId) => {
    try {
      const { data: mileageLog } = await secureAxios.get(
        `/api/mileage/route/${routeId}`
      );
      setAssignedConsumerId(mileageLog.consumer_id);
      fetchAuthorizations(mileageLog.consumer_id);
      setLoadedRouteId(routeId);
    } catch (err) {
      setAssignedConsumerId(null);
      setLoadedRouteId(routeId);
    }
  };

  return (
    <div className="container mt-4">
      <MapContainer
        center={[38.4192, -82.4452]}
        zoom={12}
        style={{ height: "400px", width: "100%" }}
        attributionControl={false}
        whenCreated={(mapInstance) => {
          mapRef.current = mapInstance;
        }}
      >
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <RoutingControl
          ref={routingRef}
          waypoints={waypoints}
          onRouteFound={handleRouteFound}
        />
      </MapContainer>
      {/* Edit Mode Indicator */}
      {editId && (
        <div className="alert alert-info mt-3">
          <i className="bi bi-pencil-square me-2"></i>
          <strong>Edit Mode:</strong> You are editing an existing mileage log.
        </div>
      )}
      <div className="container mt-4">
        {/* Row 1 - Stop Selection */}
        <div className="row g-3 align-items-end mb-5 border rounded p-3">
          <h6 className="text-muted mb-2">
            <i className="bi bi-geo-alt me-1"></i>Add Route Stops
          </h6>

          <div className="col-md-4 d-flex align-items-end">
            <div className="w-100 me-2">
              <label className="form-label">Select Job Coach</label>
              <select
                className="form-select"
                value={selectedCoach}
                onChange={(e) => setSelectedCoach(e.target.value)}
              >
                <option value="">-- Select --</option>
                {user?.is_admin
                  ? users.map((u) => (
                      <option key={`coach-${u.user_id}`} value={u.user_id}>
                        {u.name}
                      </option>
                    ))
                  : users
                      .filter((u) => u.user_id === user.user_id)
                      .map((u) => (
                        <option key={`coach-${u.user_id}`} value={u.user_id}>
                          {u.name}
                        </option>
                      ))}
              </select>
            </div>
            <button
              className="btn btn-outline-primary"
              onClick={() => handleAddStop("user", selectedCoach)}
              disabled={!selectedCoach}
              title="Add job coach location to route"
            >
              <i className="bi bi-plus-lg"></i>
            </button>
          </div>

          <div className="col-md-4 d-flex align-items-end">
            <div className="w-100 me-2">
              <label className="form-label">
                Select Consumer
                {consumers.length > 0 && (
                  <span className="badge bg-info ms-2">PHI</span>
                )}
              </label>
              <select
                className="form-select"
                value={selectedConsumer}
                onChange={(e) => setSelectedConsumer(e.target.value)}
              >
                <option value="">-- Select --</option>
                {consumers.map((c) => (
                  <option
                    key={`consumer-${c.consumer_id}`}
                    value={c.consumer_id}
                  >
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-outline-primary"
              onClick={() => handleAddStop("consumer", selectedConsumer)}
              disabled={!selectedConsumer}
              title="Add consumer location to route"
            >
              <i className="bi bi-plus-lg"></i>
            </button>
          </div>

          <div className="col-md-4 d-flex align-items-end">
            <div className="w-100 me-2">
              <label className="form-label">Select Job Location</label>
              <select
                className="form-select"
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                <option value="">-- Select --</option>
                {locations.map((loc) => (
                  <option
                    key={`location-${loc.location_id}`}
                    value={loc.location_id}
                  >
                    {loc.location_name}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-outline-primary"
              onClick={() => handleAddStop("location", selectedLocation)}
              disabled={!selectedLocation}
              title="Add location to route"
            >
              <i className="bi bi-plus-lg"></i>
            </button>
          </div>
        </div>

        {/* Row 2 - Saved Routes */}
        <div className="row g-3 align-items-end mb-5 border rounded p-3">
          <h6 className="text-muted mb-2">
            <i className="bi bi-save me-1"></i>Load Saved Route
          </h6>

          <div className="col-md-8">
            <label className="form-label">Select Saved Route</label>
            <select
              className="form-select"
              value={selectedSavedRouteId}
              onChange={(e) => setSelectedSavedRouteId(e.target.value)}
            >
              <option value="">-- Select Saved Route --</option>
              {savedRoutes.map((route) => (
                <option key={route.route_id} value={route.route_id}>
                  {route.route_name} ({route.total_miles} mi)
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4 d-flex gap-2">
            <div className="d-grid w-50">
              <label className="form-label invisible">Load</label>
              <button
                className="btn btn-secondary"
                onClick={handleLoadRoute}
                disabled={!selectedSavedRouteId}
              >
                Load
              </button>
            </div>
            <div className="d-grid w-50">
              <label className="form-label invisible">Delete</label>
              <button
                className="btn btn-danger"
                onClick={handleDeleteRoute}
                disabled={!selectedSavedRouteId}
              >
                Delete
              </button>
            </div>
          </div>
        </div>

        {/* Row 3 - Save Route */}
        <div className="row g-3 align-items-end mb-5 border rounded p-3">
          <h6 className="text-muted mb-2">
            <i className="bi bi-map me-1"></i>Save Current Route
          </h6>

          <div className="col-md-4">
            <label className="form-label">Route Name</label>
            <input
              className="form-control"
              value={routeName}
              onChange={(e) => setRouteName(e.target.value)}
              placeholder="Route name"
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">
              Assign To Consumer
              <span className="badge bg-info ms-2">PHI</span>
            </label>
            <select
              className="form-select"
              value={selectedAssignedConsumer}
              onChange={(e) => setSelectedAssignedConsumer(e.target.value)}
            >
              <option value="">-- Select Consumer to Assign Route --</option>
              {consumers.map((c) => (
                <option key={c.consumer_id} value={c.consumer_id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2 d-grid">
            <label className="form-label invisible">Save</label>
            <button
              className="btn btn-primary"
              onClick={handleSaveRoute}
              disabled={distance === null}
            >
              Save Route
            </button>
          </div>
        </div>
      </div>

      {/*Mileage Log Section*/}
      {waypoints.length > 0 && (
        <div className="card mt-4 p-3 shadow-sm">
          <h5 className="mb-3">
            <i className="bi bi-speedometer2 me-2"></i>Mileage Log
          </h5>

          {/* PHI Access Notice for billable mileage */}
          {isBillable && <PHIAccessNotice />}

          <div className="row g-3 align-items-end">
            {user?.is_admin && (
              <div className="col-md-3">
                <label className="form-label">Job Coach</label>
                <select
                  className="form-select"
                  value={selectedCoach}
                  onChange={(e) => setSelectedCoach(e.target.value)}
                  disabled={showAllConsumers}
                >
                  <option value="">-- Select Job Coach --</option>
                  {users.map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.name}
                    </option>
                  ))}
                </select>
                {showAllConsumers && (
                  <small className="text-muted">
                    Disabled when showing all consumers
                  </small>
                )}
              </div>
            )}

            {/* Admin toggle to show all consumers */}
            {user?.is_admin && isBillable && (
              <div className="col-md-3">
                <div className="form-check mt-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="showAllConsumersMileage"
                    checked={showAllConsumers}
                    onChange={(e) => {
                      setShowAllConsumers(e.target.checked);
                      // Reset consumer selection when toggling
                      setSelectedMileageConsumer("");
                      setSelectedAuthorization("");
                      setAuthOptions([]);
                      setAuthorizationWarning(null);
                    }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="showAllConsumersMileage"
                  >
                    Show All Consumers
                  </label>
                </div>
                <small className="text-muted">
                  {showAllConsumers
                    ? "Showing all active consumers"
                    : "Showing assigned consumers only"}
                </small>
              </div>
            )}

            <div className="col-md-3">
              <label className="form-label">Trip Date</label>
              <input
                type="date"
                className="form-control"
                value={tripDate}
                onChange={(e) => setTripDate(e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <div className="form-check mt-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="isBillable"
                  checked={isBillable}
                  onChange={(e) => {
                    setIsBillable(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedMileageConsumer("");
                      setSelectedAuthorization("");
                      setAuthOptions([]);
                      setAuthorizationWarning(null);
                      setPurposeOfUse("treatment"); // Reset to default
                    }
                  }}
                />
                <label className="form-check-label" htmlFor="isBillable">
                  Billable Mileage
                </label>
              </div>
              {!isBillable && (
                <small className="text-muted">
                  Non-billable time does not require consumer or authorization
                </small>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Reason for Travel <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control"
                value={reasonForTravel}
                onChange={(e) => setReasonForTravel(e.target.value)}
                placeholder="e.g. Transport to job site, return home, etc."
                rows={2}
                required
              />
            </div>

            {isBillable && !assignedConsumerId && (
              <div className="col-md-3">
                <label className="form-label">
                  Consumer <span className="badge bg-info ms-1">PHI</span>
                </label>
                <select
                  className="form-select"
                  value={selectedMileageConsumer}
                  onChange={(e) => {
                    const cid = e.target.value;
                    setSelectedMileageConsumer(cid);
                    fetchAuthorizations(cid);
                  }}
                >
                  <option value="">-- Select Consumer --</option>
                  {consumers.map((c) => (
                    <option key={c.consumer_id} value={c.consumer_id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {isBillable && (
              <div className="col-md-3">
                <label className="form-label">Authorization</label>
                <select
                  className="form-select"
                  value={selectedAuthorization}
                  onChange={(e) => setSelectedAuthorization(e.target.value)}
                >
                  <option value="">-- Select Authorization --</option>
                  {authOptions.map((auth) => (
                    <option
                      key={auth.authorization_id}
                      value={auth.authorization_id}
                    >
                      {auth.authorization_number}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="col-md-2">
              <label className="form-label">Total Miles</label>
              <input
                className="form-control"
                value={Math.ceil(distance || 0)}
                disabled
              />
            </div>

            <div className="col-md-2">
              <button
                className="btn btn-success w-100"
                onClick={handleSaveMileageLog}
                disabled={
                  authorizationWarning && authorizationWarning.remaining <= 0
                }
              >
                Save Mileage Log
              </button>
            </div>
          </div>

          {authorizationWarning && (
            <div
              className={`alert mt-3 ${
                authorizationWarning.remaining <= 0
                  ? "alert-danger"
                  : authorizationWarning.remaining < 30
                  ? "alert-warning"
                  : "alert-success"
              }`}
            >
              {authorizationWarning.remaining <= 0 ? (
                <>
                  <strong>No miles left!</strong> This authorization has no
                  remaining miles.
                  {authorizationWarning.allowPartial &&
                    authorizationWarning.remaining < 0 && (
                      <div className="mt-2">
                        This authorization is{" "}
                        {Math.abs(authorizationWarning.remaining).toFixed(1)}{" "}
                        miles over the limit.
                      </div>
                    )}
                </>
              ) : authorizationWarning.remaining < 30 ? (
                <>
                  <strong>Warning:</strong> Only{" "}
                  {authorizationWarning.remaining.toFixed(1)} miles left on this
                  authorization.
                  {!authorizationWarning.valid && (
                    <div className="mt-2">
                      You are trying to log {authorizationWarning.requested}{" "}
                      miles, which exceeds the remaining amount.
                    </div>
                  )}
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle-fill text-success me-1"></i>{" "}
                  {authorizationWarning.remaining.toFixed(1)} miles left on this
                  authorization.
                  {!authorizationWarning.valid && (
                    <div className="mt-2">
                      You are trying to log {authorizationWarning.requested}{" "}
                      miles, which exceeds the remaining amount.
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {mileageMessage && (
            <div
              className={`mt-3 ${
                mileageMessage.includes("Failed") ||
                mileageMessage.includes("Missing")
                  ? "text-danger"
                  : "text-success"
              }`}
            >
              {mileageMessage}
            </div>
          )}
        </div>
      )}

      {/* Current Stops */}
      <div className="mt-4">
        <h5>
          <i className="bi bi-list-ol me-2"></i>Current Stops
        </h5>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={waypoints.map((wp) => wp.id)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="list-group">
              {waypoints.map((wp, index) => (
                <SortableItem
                  key={wp.id}
                  wp={wp}
                  index={index}
                  onRemove={() => handleRemoveStop(wp.id)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>

        {distance && (
          <div className="alert alert-success mt-3">
            <strong>Route Summary:</strong> {distance} miles | {routeTime}{" "}
            minutes
          </div>
        )}
      </div>

      {/* Routing Instructions */}
      <div className="mt-3">
        <h6>
          <i className="bi bi-signpost-2 me-2"></i>Turn-by-Turn Directions
        </h6>
        <div
          id="routing-instructions"
          className="border rounded p-3 bg-light"
          style={{ maxHeight: "300px", overflowY: "auto" }}
        />
      </div>
    </div>
  );
};

export default RoutePlanner;
